'use client'

import { useMemo, useRef, useState } from 'react'
import { getImageProps } from 'next/image'
import clsx from 'clsx'
import { AnimatePresence, motion, useInView, useMotionValueEvent, useScroll } from 'motion/react'
import { useTranslation } from 'react-i18next'
import * as GridLayout from '@/components/GridLayout/GridLayout'
import type { VideoProps } from '@/components/Video/Video'
import { Video } from '@/components/Video/Video'
import type { GridColumnsField, SbBaseBlockProps, StoryblokAsset } from '@/services/storyblok/storyblok'
import { getImgSrc } from '@/services/storyblok/Storyblok.helpers'
import { useBreakpoint } from '@/utils/useBreakpoint/useBreakpoint'
import { Button } from 'ui/src/components/Button/Button'
import { ConditionalWrapper } from 'ui/src/components/ConditionalWrapper'
import { ArrowDownIcon } from 'ui/src/icons/ArrowIcon'
import { getMediaQueryBreakpoint } from 'ui/src/theme/media'
import { useSmoothAnchorScroll } from 'ui/src/utils/useSmoothAnchorScroll'
import { SCROLL_TO_SECTION_BUTTON_BOTTOM_GAP, SCROLL_TO_SECTION_BUTTON_HEIGHT, scrollToButton } from './VideoBlock.css'

export type VideoBlockProps = SbBaseBlockProps<
  {
    videoUrlWebm?: string
    videoUrl: string
    poster?: StoryblokAsset
    fullBleed?: boolean
    controls?: boolean
    layout?: GridColumnsField
    scrollTo?: string
    scrollToButtonLabel?: string
    showScrollToButtonInMobile: boolean
    /**
     * Accessible title describing the video content.
     * Used in screen reader announcements for play/pause buttons.
     */
    videoTitle?: string
  } & Pick<
    VideoProps,
    | 'autoPlay'
    | 'aspectRatioLandscape'
    | 'aspectRatioPortrait'
    | 'maxHeightLandscape'
    | 'maxHeightPortrait'
    | 'horizontalOffsetPortrait'
    | 'hideSoundControl'
    | 'transcription'
  >
> & { className?: string }

export function VideoBlock({ className, blok, nested = false }: VideoBlockProps) {
  const { t } = useTranslation()
  const isLarge = useBreakpoint('lg')
  const handleSmoothScroll = useSmoothAnchorScroll()

  const topSentinelRef = useRef<HTMLDivElement>(null)
  const bottomSentinelRef = useRef<HTMLDivElement>(null)
  const rootMargin = `0px 0px -${SCROLL_TO_SECTION_BUTTON_HEIGHT + SCROLL_TO_SECTION_BUTTON_BOTTOM_GAP}px 0px`
  const isTopSentinelInView = useInView(topSentinelRef, { margin: rootMargin, amount: 'some' })
  const isBottomSentinelInView = useInView(bottomSentinelRef, { margin: rootMargin, amount: 'all' })
  const scrollDirection = useScrollDirection()
  const show = useMemo(() => {
    if (scrollDirection === 'down') {
      return isBottomSentinelInView ? false : isTopSentinelInView
    }

    if (scrollDirection === 'up') {
      return !isBottomSentinelInView && isTopSentinelInView
    }
  }, [scrollDirection, isTopSentinelInView, isBottomSentinelInView])

  const videoSources = [...(blok.videoUrlWebm ? [{ url: blok.videoUrlWebm }] : []), { url: blok.videoUrl }]
  const posterImg = blok.poster?.filename
  const posterUrl = posterImg
    ? getImageProps({
        src: getImgSrc(posterImg),
        width: getMediaQueryBreakpoint('lg'),
        height: getMediaQueryBreakpoint('lg'),
        alt: '',
      }).props.src
    : undefined

  const isJumpToButtonEnabled = (isLarge || blok.showScrollToButtonInMobile) && blok.scrollTo

  return (
    <ConditionalWrapper
      condition={!(blok.fullBleed || nested)}
      wrapWith={(children) => (
        <GridLayout.Root className={className}>
          <GridLayout.Content width={blok.layout?.widths ?? { _: '1' }} align={blok.layout?.alignment ?? 'center'}>
            {children}
          </GridLayout.Content>
        </GridLayout.Root>
      )}
    >
      <div style={{ position: 'relative' }}>
        <div
          id="top-sentinel"
          ref={topSentinelRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: SCROLL_TO_SECTION_BUTTON_HEIGHT,
            pointerEvents: 'none',
          }}
        />
        <Video
          id={blok._uid}
          sources={videoSources}
          poster={posterUrl}
          autoPlay={blok.autoPlay}
          aspectRatioLandscape={blok.aspectRatioLandscape}
          aspectRatioPortrait={blok.aspectRatioPortrait}
          maxHeightLandscape={blok.maxHeightLandscape}
          maxHeightPortrait={blok.maxHeightPortrait}
          horizontalOffsetPortrait={blok.horizontalOffsetPortrait}
          roundedCorners={!blok.fullBleed}
          showControls={blok.controls}
          hideSoundControl={blok.hideSoundControl}
          title={blok.videoTitle}
          transcription={blok.transcription}
        />
        <div
          id="bottom-sentinel"
          ref={bottomSentinelRef}
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: SCROLL_TO_SECTION_BUTTON_HEIGHT,
            pointerEvents: 'none',
          }}
        />
      </div>
      {isJumpToButtonEnabled && (
        <AnimatePresence>
          {show && (
            <Button
              className={clsx(scrollToButton)}
              as={motion.a}
              href={`#${blok.scrollTo}`}
              onClick={handleSmoothScroll}
              variant="primary"
              size="medium"
              Icon={<ArrowDownIcon size="1.5rem" />}
              iconPosition="right"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {blok.scrollToButtonLabel || t('VIDEO_BLOCK_SCROLL_TO_SECTION_LABEL')}
            </Button>
          )}
        </AnimatePresence>
      )}
    </ConditionalWrapper>
  )
}

function useScrollDirection() {
  const { scrollY } = useScroll()
  const [scrollDirection, setScrollDirection] = useState('down')

  useMotionValueEvent(scrollY, 'change', (current) => {
    const diff = current - (scrollY.getPrevious() ?? 0)
    setScrollDirection(diff > 0 ? 'down' : 'up')
  })

  return scrollDirection
}
